import { ChevronsUpDown } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useDismissed } from "./dismiss";
import { Input } from "./input";
import { Listbox, type ComboboxOption, useCombobox } from "./TypeAhead";

export interface SwitcherChoice {
  key: string;
  label: string;
}

const PANEL =
  "absolute top-[calc(100%+6px)] left-0 z-40 flex w-[280px] flex-col gap-1 rounded-menu border border-border bg-card p-[5px] text-left font-normal text-foreground shadow-popover";

/**
 * One of a set, swapped for another by name: the client a page is about, or
 * the draft open under it.
 *
 * A trigger opens a search box over the set, which narrows as the attorney
 * types. The box is a combobox over a listbox, so the focus stays in the box
 * and the arrows move which choice is in hand; Enter or a press takes it.
 * Taking the current one again changes nothing. It is a popover rather than a
 * menu: what it offers are the things themselves, not acts on one.
 */
export function Switcher({
  name,
  label,
  choices,
  currentKey,
  onChoose,
  current = false,
  className,
}: {
  /** What the trigger does, as a reader hears it: "Switch client". */
  name: string;
  /** The words the trigger carries, where it is the current step itself. */
  label?: string;
  choices: readonly SwitcherChoice[];
  currentKey: string;
  onChoose: (key: string) => void;
  /** Whether the trigger stands for the page being read, as the last step of a trail does. */
  current?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismissed(open, panelRef, triggerRef, close);

  function take(key: string) {
    setOpen(false);
    triggerRef.current?.focus();
    if (key !== currentKey) onChoose(key);
  }

  return (
    <span className="relative inline-flex min-w-0">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label === undefined ? name : `${label}, ${name}`}
        aria-haspopup="listbox"
        aria-current={current ? "page" : undefined}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
        className={cn(
          "inline-flex min-w-0 cursor-pointer items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
          className,
        )}
      >
        {label !== undefined && <span className="truncate">{label}</span>}
        <ChevronsUpDown aria-hidden className="size-3.5 shrink-0" />
      </button>
      {open && (
        <div ref={panelRef} className={PANEL}>
          <SwitcherSearch
            name={name}
            choices={choices}
            currentKey={currentKey}
            onTake={take}
          />
        </div>
      )}
    </span>
  );
}

/**
 * The search box and what it narrows to. Mounted on each opening, so what was
 * typed and which choice was in hand start afresh, from the current one. The
 * keyboard and the list are the shared combobox's, as every such box draws them.
 */
function SwitcherSearch({
  name,
  choices,
  currentKey,
  onTake,
}: {
  name: string;
  choices: readonly SwitcherChoice[];
  currentKey: string;
  onTake: (key: string) => void;
}) {
  const [query, setQuery] = useState("");
  const shown: ComboboxOption[] = choices
    .filter((choice) =>
      choice.label.toLowerCase().includes(query.trim().toLowerCase()),
    )
    .map((choice) => ({ ...choice, current: choice.key === currentKey }));
  const combo = useCombobox({
    options: shown,
    initial: Math.max(
      0,
      choices.findIndex((choice) => choice.key === currentKey),
    ),
    onTake: (choice) => {
      onTake(choice.key);
    },
  });

  return (
    <>
      <Input
        size="dense"
        {...combo.inputProps}
        aria-label="Search"
        aria-expanded
        autoFocus
        value={query}
        placeholder="Search"
        onChange={(event) => {
          setQuery(event.target.value);
          combo.setActive(0);
        }}
        onKeyDown={(event) => {
          combo.onKeyDown(event);
        }}
      />
      <Listbox
        listId={combo.listId}
        name={name}
        options={shown}
        active={combo.active}
        optionId={combo.optionId}
        onHover={combo.setActive}
        onTake={(choice) => {
          onTake(choice.key);
        }}
        empty="No matches"
      />
    </>
  );
}
